import {
  createActionsColumn,
  TableCellProps,
  WbBadge,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { Box, makeStyles, Typography } from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import { useMemo } from 'react';
import {
  TeamRoleAssignment,
  TeamRoleAssignmentKindEnum,
  TeamRoleAssignmentKindAsString,
  TeamRoleAsString,
} from '../types';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useTeamRolesPanelContext } from '../context';
import { TeamRoleAssignmentsActionsColumn } from '../components';
import PersonIcon from '@material-ui/icons/Person';
import GroupIcon from '@material-ui/icons/Group';

const teamRoleAssignmentKindDescription: Record<
  TeamRoleAssignmentKindEnum,
  (teamRole: string) => string
> = {
  [TeamRoleAssignmentKindEnum.ASSIGNED_BY_THE_PLATFORM]: (teamRole: string) =>
    `User or group is not assigned to the "${teamRole}" Team Role, but it inherits the permissions required. It cannot be managed directly from this panel.`,
  [TeamRoleAssignmentKindEnum.LIMITED]: (teamRole: string) =>
    `User or group has a limited assignment for the "${teamRole}" Team Role.`,
  [TeamRoleAssignmentKindEnum.FULL]: (teamRole: string) =>
    `User or group has a full assignment for the "${teamRole}" Team Role.`,
};

const useStyles = makeStyles(theme => ({
  warningIcon: {
    color: theme.palette.warning.main,
  },
}));

export const useTeamRoleAssignmentsColumns = ({
  onDelete,
}: {
  onDelete: (assignment: TeamRoleAssignment) => void;
}) => {
  const classes = useStyles();
  const { selectedTeamRole, canHaveLimitedAssignees } =
    useTeamRolesPanelContext();

  return useMemo<TableCellProps<TeamRoleAssignment>[]>(
    () => [
      {
        headerName: 'Assignee',
        field: 'subject',
        fieldRender: assignment => (
          <Box style={{ display: 'flex', alignItems: 'center', padding: 8 }}>
            {assignment.kinds.some(
              k => k === TeamRoleAssignmentKindEnum.ASSIGNED_BY_THE_PLATFORM,
            ) && (
              <Box style={{ paddingRight: 8 }}>
                <LockIcon />
              </Box>
            )}
            {assignment.subject.urn.startsWith('group') ? (
              <GroupIcon fontSize="small" style={{ marginRight: 8 }} />
            ) : (
              <PersonIcon fontSize="small" style={{ marginRight: 8 }} />
            )}
            {assignment.subject.displayName || assignment.subject.urn}
          </Box>
        ),
      },
      ...(canHaveLimitedAssignees
        ? ([
            {
              headerName: 'Kind',
              field: 'kinds',
              fieldRender: (assignment: TeamRoleAssignment) => {
                const hasFull = assignment.kinds.includes(
                  TeamRoleAssignmentKindEnum.FULL,
                );
                const hasLimited = assignment.kinds.includes(
                  TeamRoleAssignmentKindEnum.LIMITED,
                );
                const displayKinds =
                  hasFull || hasLimited
                    ? assignment.kinds.filter(
                        k =>
                          k !==
                          TeamRoleAssignmentKindEnum.ASSIGNED_BY_THE_PLATFORM,
                      )
                    : assignment.kinds;

                return (
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: 8,
                      gap: 8,
                    }}
                  >
                    {displayKinds.map(kind => (
                      <WbTooltip
                        title={teamRoleAssignmentKindDescription[kind](
                          TeamRoleAsString[selectedTeamRole],
                        )}
                      >
                        <span style={{ display: 'inline-block' }}>
                          {kind !==
                            TeamRoleAssignmentKindEnum.ASSIGNED_BY_THE_PLATFORM && (
                            <WbBadge
                              color={
                                kind === TeamRoleAssignmentKindEnum.LIMITED
                                  ? 'secondary'
                                  : undefined
                              }
                            >
                              {TeamRoleAssignmentKindAsString[kind]}
                            </WbBadge>
                          )}
                          {kind ===
                            TeamRoleAssignmentKindEnum.ASSIGNED_BY_THE_PLATFORM && (
                            <Typography style={{ fontStyle: 'italic' }}>
                              {TeamRoleAssignmentKindAsString[kind]}
                            </Typography>
                          )}
                        </span>
                      </WbTooltip>
                    ))}
                    {hasFull && hasLimited && (
                      <WbTooltip
                        title={`This user or group has both the full and limited permissions required by "${TeamRoleAsString[selectedTeamRole]}" Team Role. Please check with the Platform Team if this is a misconfiguration issue.`}
                      >
                        <ErrorOutlineIcon className={classes.warningIcon} />
                      </WbTooltip>
                    )}
                  </Box>
                );
              },
            },
          ] as TableCellProps<TeamRoleAssignment>[])
        : []),
      createActionsColumn({
        renderActions: row => (
          <TeamRoleAssignmentsActionsColumn
            assignment={row}
            onDelete={onDelete}
          />
        ),
      }),
    ],
    [classes, selectedTeamRole, onDelete, canHaveLimitedAssignees],
  );
};
